// Mixins
import { toastMessageMixins } from "@/app/mixins/utils/toast-message-mixins"
import { tableMixins } from "@/app/mixins/utils/table-mixins"
import { dateMixins } from "@/app/mixins/utils/date-mixins"

// Services
import OrdersService from "@/app/services/main/orders/orders-service"

// Constants
import { STATUS_CODES, TRANSACTION_STATUS } from "@/app/constants/constants"

// Components
const ModalOrdersModifyTrackingNumber = () =>
	import(
		/* webpackPrefetch: true, webpackChunkName: "modal-orders-modify-tracking-number"*/ "@/app/views/main-view/orders/containers/modals/modal-orders-modify-tracking-number/modal-orders-modify-tracking-number.vue"
	)
const ModalDisapproveCancellationRequest = () =>
	import(
		/* webpackPrefetch: true, webpackChunkName: "modal-disapprove-cancellation-request"*/ "@/app/views/main-view/orders/containers/modals/modal-disapprove-cancellation-request/modal-disapprove-cancellation-request.vue"
	)

export default {
	name: "OrderDetailsContainer",
	components: {
		ModalOrdersModifyTrackingNumber,
		ModalDisapproveCancellationRequest,
	},
	mixins: [toastMessageMixins, tableMixins, dateMixins],
	props: {},
	data() {
		return {
			isAPILoading: false,
			orderDetails: {
				paymentMethod: [],
			},
			payment: [],
			isPaymentLoading: false,
			productBundle: [{ isCustomBundle: false }],
			// table
			theads: [
				{ name: "SKU", sortLabel: null, isDesc: false, style: null },
				{ name: "Image", sortLabel: null, isDesc: false, style: null },
				{ name: "Products", sortLabel: null, isDesc: false, style: null },
				{ name: "Quantity", sortLabel: null, isDesc: false, style: null },
				{ name: "Price", sortLabel: null, isDesc: false, style: null },
				{ name: null, sortLabel: null, isDesc: false, style: null },
			],
			paymentTheads: [
				{ name: "Payment Method", sortLabel: null, isDesc: false, style: null },
				{ name: "Status", sortLabel: null, isDesc: false, style: null },
				{ name: "Message", sortLabel: null, isDesc: false, style: null },
				{ name: "Price", sortLabel: null, isDesc: false, style: null },
			],
			modifyMode: "",
			selectedOrder: {},
			isApproveAPILoading: false,
			isDisapproveAPILoading: false,
			activeProductId: null,
			activeProductIndex: 0,
			actionFrom:'',
			isFromSubscription: false
		}
	},
	computed: {
		timelineItems() {
			let items = []

			if (this.orderDetails && this.orderDetails.transactionHistory) {
				for (const data of this.orderDetails.transactionHistory) {
					const { date, status, remarks, submittedBy } = data

					let transDate = this.utcConvertToZonedTime(date, 'dd-MMM-yyyy hh:mm a', true)
					let content = status
					if (status === "For cancellation") {
						content += `<br> <p class="color-warning mb-0">Reason: ${remarks}</p>`
						content += `<p class="color-warning mb-0">Submitted By: ${submittedBy}</p>`;
					}
					else if (data.status === "Disapproved cancellation") {
						content += `<br> <p class="color-warning mb-0">Reason: ${remarks}</p>`;
						content += `<p class="color-warning mb-0">Verified By: ${submittedBy}</p>`;
					}

					if (status === "For delivery" && !!remarks) {
						content += `<br> <p class="color-warning mb-0">Remarks: ${remarks}</p>`;
						content += `<p class="color-warning mb-0">Verified By: ${submittedBy}</p>`;
					}
					
					items.push({
						htmlMode: true,
						content: `[${transDate}] : ${content}`,
					})
				}
			}

			return items.reverse()
		},
	},
	created() {
		this.isFromSubscription = this.$route.query.actionFrom === 'user-subscription'
		this.getOrderDetails()
	},
	methods: {
		showUpdateStatusConfirmationModal(orderId, status) {
			const orderStatus =
				status === TRANSACTION_STATUS.DELIVERED ? TRANSACTION_STATUS.DELIVERED : TRANSACTION_STATUS.REFUNDED
			let modalContent = ""
			if (orderStatus === TRANSACTION_STATUS.DELIVERED) {
				modalContent = `<p>Are you sure you want to mark order number <strong>${orderId}</strong> as <strong>${orderStatus.toUpperCase()}</strong>?</p>`
			} else {
				modalContent =
					'<p class="color-warning mb-0 my-2" >Are you sure that the LatitudePay payment has been successfully refunded to the user?</p>'
			}
			this.$swal({
				titleText: `Mark Order as ${orderStatus}`,
				html: modalContent,
				confirmButtonText: "Confirm",
				showCancelButton: true,
				cancelButtonText: "Cancel",
				reverseButtons: true,
				showCloseButton: true
			}).then((result) => {
				if (result.value) {
					this.isAPILoading = true

					const bodyParams = { status: orderStatus }
					const {
						SUCCESS_MARKED_ORDER_AS_DELIVERED,
						SUCCESS_LATITUDE_PAY_REFUND,
						ERROR_MARK_ORDER_AS_DELIVERED,
						ERROR_MARK_ORDER_AS_REFUNDED,
					} = this.toastMessages.order()

					const successMessage =
						status === TRANSACTION_STATUS.DELIVERED
							? SUCCESS_MARKED_ORDER_AS_DELIVERED
							: SUCCESS_LATITUDE_PAY_REFUND
					const errorMessage =
						status === TRANSACTION_STATUS.DELIVERED
							? ERROR_MARK_ORDER_AS_DELIVERED
							: ERROR_MARK_ORDER_AS_REFUNDED

					// Call the API endpoint to update the order status
					OrdersService.putUpdateOrderStatus(orderId, bodyParams).then((res) => {
						if (res.status === STATUS_CODES.SUCCESS_NO_CONTENT) {
							// Refresh page
							this.getOrderDetails()

							// Show success message
							this.showCustomSuccessToast(successMessage)
						} else {
							// Show error message
							this.showAPIErrorToast(errorMessage, res)
						}

						this.isAPILoading = false
					})
				}
			})
		},
		getOrderDetails() {
			this.isAPILoading = true
			OrdersService.getOrderById(this.$route.params.orderId)
				.then((res) => {
					if (res.status === STATUS_CODES.SUCCESS) {
						this.orderDetails = res.data
						this.productBundle = res.data.bundles
						this.payment = res.data.payment.map((dt, idx) => {
							return { ...dt, index: idx }
						})

						const currentPayments = this.payment;
						// sort payment history via status alphabetically
						const currentPaymentsSorted = currentPayments.sort((a, b) => {
							let sa = a.status.toLowerCase(),
							sb = b.status.toLowerCase();
							if (sa > sb) {
								return -1;
							}
							if (sa < sb) {
								return 1;
							}
							return 0;
						})
						
						const ids = currentPaymentsSorted.map(({ name }) => name);
						
						const payments = currentPaymentsSorted.filter(({ name }, index) => !ids.includes(name, index + 1));

						this.payment = payments;

					} else {
						this.showAPIErrorToast("Unable to get order data.", res)
					}

					this.isAPILoading = false
				})
				.catch(() => {
					this.showGenericErrorToast()
					this.isAPILoading = false
				})
		},

		formatMobileNumber(number) {
				let data = number.split("")
				data.splice(4, 0, "-")
				return data.join("")
		},
		showModifyTrackingModal(order, mode) {
			this.selectedOrder = order
			this.modifyMode = mode

			setTimeout(() => {
				this.$refs.modalModifyTracking.openModal()
			}, 50)
		},
		submitApproval() {
			let modalContent = '<div class="d-flex align-items-center flex-column">'
			modalContent += '<p class="color-warning">Are you sure you want to approve the cancellation request?</p>'
			modalContent += '<p class="mb-1">Amount to be refunded to the customer:</p>'
			
			modalContent += this.paymentMethodFormat();
			
			modalContent += "</div>"
			this.$swal({
				titleText: "Cancel Order",
				html: modalContent,
				confirmButtonText: "Confirm",
				showCancelButton: true,
				cancelButtonText: "Cancel",
				reverseButtons: true,
				showCloseButton: true
			}).then((result) => {
				if (result.value) {
					this.isApproveAPILoading = true
					let service = "putApproveCancelOrderStoreCredit"
					if (this.orderDetails.paymentMethod.some((e) => e.name === "Latitude Interest Free")) {
						service = "putApproveRefund"
					} else if (this.orderDetails.paymentMethod.some((e) => e.name === "Stripe")) {
						service = "putApproveCancelOrder"
					}

					// this true if the split payment one of it is split payment
					const totalOrderPrice = isNaN(this.orderDetails.payment[0].price) ? 0 : parseFloat(this.orderDetails.payment[0].price)
					const deliveryFee = this.orderDetails.deliveryFee === null ? 0: parseFloat(this.orderDetails.deliveryFee)
					const totalPrice = totalOrderPrice - deliveryFee;

					OrdersService[service](this.orderDetails.id, {
						products: this.orderDetails.bundles,
						totalPrice,
						paymentInfo: this.orderDetails.payment[0],
						cancellationStatusId: this.orderDetails.cancellationStatusId

					})
						.then((res) => {
							const { SUCCESS_APPROVE_CANCELLATION, ERROR_APPROVE_CANCELLATION } =
								this.toastMessages.order()

							if (res.status === STATUS_CODES.SUCCESS_NO_CONTENT || res.status === STATUS_CODES.SUCCESS) {
								this.showCustomSuccessToast(SUCCESS_APPROVE_CANCELLATION, "")
								this.getOrderDetails()
							} else {
								this.showAPIErrorToast(ERROR_APPROVE_CANCELLATION, res)
							}
						})
						.catch(() => {
							this.showGenericErrorToast()
						})
						.finally(() => {
							this.isApproveAPILoading = false
						})
				}
			})
		},
		paymentMethodFormat(){
			let paymentContent = "";

			if(this.orderDetails.paymentMethod.length!=1){
				for (const paymentMethod of this.orderDetails.paymentMethod) {
					if(paymentMethod.name.toUpperCase() === "STRIPE" ){
						paymentContent += `<span class="mr-2 font-weight-bold text-right">${paymentMethod.name.toUpperCase()}: $${
							(parseFloat(paymentMethod.amount) - parseFloat(this.orderDetails.deliveryFee ? this.orderDetails.deliveryFee : 0)).toFixed(2)
						}</span>`
					}else{
						paymentContent += `<span class="mr-2 font-weight-bold text-right">${paymentMethod.name.toUpperCase()}: $${parseFloat(paymentMethod.amount)}</span>`
					}

				}
			}else{
				for (const paymentMethod of this.orderDetails.paymentMethod) {
					paymentContent += `<span class="mr-2 font-weight-bold text-right">${paymentMethod.name.toUpperCase()}: $${
						(parseFloat(paymentMethod.amount) - parseFloat(this.orderDetails.deliveryFee ? this.orderDetails.deliveryFee : 0)).toFixed(2)
					}</span>`
				}
			}

			return paymentContent;

		},
		showDisapproveModal() {
			this.isDisapproveAPILoading = true
			this.$refs.modalDisapprove.openModal(this.orderDetails)
		},
		handleSuccessDisapproved() {
			this.isDisapproveAPILoading = false
			this.getOrderDetails()
		},
		onCloseModal() {
			this.isDisapproveAPILoading = false
		},
		setProductImage(img) {
			return img ? img : require("@/assets/images/_shared/product-default.png")
		},
		getImage(bundle) {
			if (bundle.isCustomBundle) {
				return !!bundle.imageUrl ? bundle.imageUrl : require("@/assets/images/_shared/product-default.png")
			} else {
				return !!bundle.products[0].imageUrl
					? bundle.products[0].imageUrl
					: require("@/assets/images/_shared/product-default.png")
			}
		},
		renderCollapsibleRow(productId) {
			return productId ? this.activeProductId === productId : false
		},
		showCollapsibleRow(productId, idx) {
			this.activeProductIndex = idx
			if (this.activeProductId === productId) {
				this.activeProductId = !this.activeProductId ? productId : null
			} else {
				this.activeProductId = productId
			}
		},
		processPendingPayment(data) {
			this.$swal({
				title: "Update Payment",
				text: `Was the client payment successful or unsuccessful?`,
				confirmButtonText: "Successful",
				showCancelButton: true,
				cancelButtonText: "Unsuccessful",
				reverseButtons: true,
				showCloseButton: true
			}).then((result) => {
				if (!result.dismiss || (result.dismiss !== 'close' && 
						result.dismiss !== 'backdrop')) {
					let status = result.value ? 'APPROVED' : '';

					if (result.dismiss === 'cancel') {
						status = 'CANCELLED'
					}
					this.isPaymentLoading = true;
					OrdersService.putUpdateOrderProductStatus(this.$route.params.orderId, status)
						.then((res) => {
							if (res.status === STATUS_CODES.SUCCESS) {
								this.showCustomSuccessToast(null, `Successfully updated the payment status.`);
								this.getOrderDetails()
							} else {
								this.showAPIErrorToast(`Failed to update the payment status.`, res);
							}
						})
						.catch(() => {
							this.showGenericErrorToast();
						})
						.finally(() => {
							this.isPaymentLoading = false;
						});
				}	
			});

		},
		subTotal (orderDetails) {
			return parseFloat(orderDetails.totalPrice) - parseFloat(orderDetails.deliveryFee ? orderDetails.deliveryFee : 0 )
		}
	},
}
